import { mayChuHoSoToChuc } from '../../mock/ho-so-to-chuc';
import { mockRequest } from '../../mock/transport';
import type {
  LogoSlot,
  TenantProfile,
  TenantProfileUpdate,
  TenantProfileVersionPage,
} from '../../modules/ho-so-to-chuc/types';
import { dungApiThat, goiApi } from '../api/http';

const GOC = '/api/v1/tenant-profile';

/** `If-Match` là phiên bản hiện hành ở dạng ETag có ngoặc kép (ADR-0059 mục 7). */
const ifMatch = (version: number) => `"${version}"`;

/** Máy chủ giả chạy **sau** nhịp chờ và các trạng thái `?mock=`: lỗi tới cùng độ trễ với lỗi thật. */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

/**
 * Hồ sơ nhận diện của tổ chức (ADR-UI-0016), qua Gateway tới tenant-service.
 * Hợp đồng ở `openapi/platform-api.yaml` (nhóm `tenant-profile`); `tenant_id` lấy từ token.
 * Mỗi hàm chọn **lúc gọi** giữa API thật và máy chủ giả.
 */
export const hoSoToChucRepository = {
  /** `GET /api/v1/tenant-profile` — mọi thành viên. `?mock=empty` là tổ chức chưa cấu hình. */
  doc: (): Promise<TenantProfile> =>
    dungApiThat()
      ? goiApi<TenantProfile>(GOC)
      : mockRequest(mayChuHoSoToChuc.doc(), mayChuHoSoToChuc.macDinhRong()),

  /** `PATCH /api/v1/tenant-profile` — lệch phiên bản trả `412`. */
  capNhat: (du: TenantProfileUpdate, version: number): Promise<TenantProfile> =>
    dungApiThat()
      ? goiApi<TenantProfile>(GOC, { method: 'PATCH', body: du, ifMatch: ifMatch(version) })
      : giaLap(() => mayChuHoSoToChuc.capNhat(du, version)),

  /** `PUT /api/v1/tenant-profile/logos/{slot}` — `multipart/form-data`, trường `file`. */
  thayLogo: (slot: LogoSlot, tep: File, version: number): Promise<TenantProfile> => {
    if (!dungApiThat()) return giaLap(() => mayChuHoSoToChuc.thayLogo(slot, tep, version));
    const bieuMau = new FormData();
    bieuMau.append('file', tep);
    return goiApi<TenantProfile>(`${GOC}/logos/${slot}`, { method: 'PUT', bieuMau, ifMatch: ifMatch(version) });
  },

  /** `DELETE /api/v1/tenant-profile/logos/{slot}` — luỹ đẳng. */
  goLogo: (slot: LogoSlot, version: number): Promise<TenantProfile> =>
    dungApiThat()
      ? goiApi<TenantProfile>(`${GOC}/logos/${slot}`, { method: 'DELETE', ifMatch: ifMatch(version) })
      : giaLap(() => mayChuHoSoToChuc.goLogo(slot, version)),

  /** `GET /api/v1/tenant-profile/versions` — mới nhất trước. */
  lichSu: ({ limit, cursor }: { limit: number; cursor?: string }): Promise<TenantProfileVersionPage> =>
    dungApiThat()
      ? goiApi<TenantProfileVersionPage>(`${GOC}/versions`, { query: { limit, cursor } })
      : mockRequest(mayChuHoSoToChuc.dsPhienBan(), { items: [], next_cursor: null }),

  /** `POST /api/v1/tenant-profile/versions/{version}/restore` — tạo phiên bản mới. */
  khoiPhuc: (phienBan: number, version: number): Promise<TenantProfile> =>
    dungApiThat()
      ? goiApi<TenantProfile>(`${GOC}/versions/${phienBan}/restore`, {
          method: 'POST',
          ifMatch: ifMatch(version),
        })
      : giaLap(() => mayChuHoSoToChuc.khoiPhuc(phienBan, version)),
};
